import React, { useMemo } from "react";

export const Card = ({ card }) => {
  const quantityText = useMemo(() => {
    const n = Number(card?.quantity);
    if (Number.isFinite(n)) return n.toLocaleString();
    return String(card?.quantity ?? "—");
  }, [card?.quantity]);

  const labelText = String(card?.name ?? "").trim() || "—";

  const hash = useMemo(() => {
    const str = labelText || "default";
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  }, [labelText]);

  const themes = [
    {
      key: "emerald",
      iconBg: "bg-emerald-50 ring-1 ring-emerald-100 text-emerald-600",
      glow: "from-emerald-300/25 via-teal-200/20 to-lime-200/25",
      qtyText: "from-emerald-600 to-teal-700",
    },
    {
      key: "indigo",
      iconBg: "bg-indigo-50 ring-1 ring-indigo-100 text-indigo-600",
      glow: "from-indigo-300/25 via-sky-200/20 to-violet-200/25",
      qtyText: "from-indigo-600 to-violet-700",
    },
    {
      key: "amber",
      iconBg: "bg-amber-50 ring-1 ring-amber-100 text-amber-600",
      glow: "from-amber-300/25 via-orange-200/20 to-rose-200/25",
      qtyText: "from-amber-700 to-orange-700",
    },
    {
      key: "rose",
      iconBg: "bg-rose-50 ring-1 ring-rose-100 text-rose-600",
      glow: "from-rose-300/25 via-pink-200/20 to-fuchsia-200/25",
      qtyText: "from-rose-600 to-pink-700",
    },
    {
      key: "cyan",
      iconBg: "bg-cyan-50 ring-1 ring-cyan-100 text-cyan-600",
      glow: "from-cyan-300/25 via-sky-200/20 to-blue-200/25",
      qtyText: "from-cyan-700 to-sky-700",
    },
    {
      key: "slate",
      iconBg: "bg-slate-50 ring-1 ring-slate-100 text-slate-700",
      glow: "from-slate-300/25 via-zinc-200/20 to-stone-200/25",
      qtyText: "from-slate-700 to-zinc-800",
    },
  ];

  const variant = themes[hash % themes.length];
  const layout = hash % 3; // 0,1,2 for small layout variety

  const align =
    layout === 0 ? "items-start text-left" : layout === 1 ? "items-center text-center" : "items-start sm:items-center sm:text-center text-left";

  const dir =
    layout === 0 ? "bg-gradient-to-tr" : layout === 1 ? "bg-gradient-to-br" : "bg-gradient-to-tl";

  return (
    <div
      role="region"
      aria-label={`${labelText}: ${quantityText}`}
      title={`${labelText}: ${quantityText}`}
      className={[
        "relative w-auto h-auto",
        "p-[1px] rounded-2xl",
        dir,
        `from-transparent via-transparent to-transparent`,
        "transition-transform duration-200",
        "hover:-translate-y-0.5 active:scale-[0.99]",
      ].join(" ")}
    >
      <div
        className={[
          "group relative rounded-2xl border border-gray-200 bg-white p-4",
          "shadow-sm transition duration-200 ease-out",
          "hover:shadow-lg hover:border-gray-300",
          "focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-black/10",
        ].join(" ")}
      >
        <div
          aria-hidden="true"
          className={[
            "pointer-events-none absolute inset-0 -z-10 rounded-2xl opacity-0",
            "transition-opacity duration-300 group-hover:opacity-100",
          ].join(" ")}
        >
          <div
            className={[
              "absolute inset-0 rounded-2xl blur-2xl",
              dir,
              variant.glow,
            ].join(" ")}
          />
        </div>

        <div className={["flex flex-col gap-3", align].join(" ")}>
          <div className="flex w-full items-center justify-between">
            <div
              className={[
                "flex h-12 w-12 items-center justify-center rounded-xl",
                variant.iconBg,
              ].join(" ")}
            >
              <span className="text-xl leading-none">{card?.icon}</span>
            </div>

            <span className="ml-3 hidden sm:inline text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              {variant.key}
            </span>
          </div>

          <div className={layout === 1 ? "mx-auto" : ""}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
              {labelText}
            </p>

            <div className="my-2 h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

            <p
              className={[
                "text-2xl font-extrabold tracking-tight",
                "bg-clip-text text-transparent",
                `bg-gradient-to-r ${variant.qtyText}`,
                "tabular-nums",
              ].join(" ")}
            >
              {quantityText}
            </p>
          </div>
        </div>

        <div
          className={[
            "pointer-events-none absolute inset-x-6 bottom-2 h-0.5",
            "bg-gradient-to-r from-transparent via-black/5 to-transparent",
            "opacity-0 transition-opacity duration-300 group-hover:opacity-100",
          ].join(" ")}
        />
      </div>
    </div>
  );
};
